import { EventEmitter, Injectable } from '@angular/core';
import { ApiErrorResponse } from '../models/api-response.model';
import { AuthResponse } from '../models/auth-response.model';
import { AuthUser } from '../models/auth-user.model';
import { ApiService } from './api.service';
import { LocalService } from './local.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly impersonationStorageKey = 'impersonation-admin-auth';
  changed: EventEmitter<boolean> = new EventEmitter();
  private currentToken: string | null = null;
  private currentUser: AuthUser | null = null;
  private impersonationAdminAuth: AuthResponse | null = null;
  private hydrationPromise: Promise<void> | null = null;

  constructor(
    private apiService: ApiService,
    private localService: LocalService,
  ) {}

  async checkValidToken(): Promise<boolean> {
    await this.hydrateAuthData();

    return new Promise((resolve, reject) => {
      const token = this.currentToken;

      if (!token) {
        this.clean();
        reject();
        return;
      }

      this.apiService
        .request<AuthResponse>(`/auth/valid/${token}`, 'GET')
        .then(async (response) => {
          await this.setAuthData(response);
          resolve(true);
        })
        .catch(async () => {
          if (this.isImpersonating() && (await this.stopImpersonation())) {
            this.checkValidToken().then(resolve).catch(reject);
            return;
          }

          this.clean();
          reject();
        });
    });
  }

  login(username: string, password: string): Promise<AuthResponse> {
    return new Promise<AuthResponse>((resolve, reject) => {
      this.apiService
        .request<AuthResponse, { username: string; password: string }>(
          '/auth/login',
          'POST',
          {
            username,
            password,
          },
        )
        .then(async (response) => {
          if (response.error) {
            reject(response);
            return;
          }

          await this.setAuthData(response);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  restorePassword(
    username: string,
    password: string,
    code: string,
  ): Promise<AuthResponse> {
    return new Promise<AuthResponse>((resolve, reject) => {
      this.apiService
        .request<
          AuthResponse,
          { username: string; password: string; code: string }
        >('/auth/restore-password', 'POST', {
          username,
          password,
          code,
        })
        .then(async (response) => {
          if (response.error) {
            reject(response);
            return;
          }

          await this.setAuthData(response);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  resetPassword(username: string): Promise<ApiErrorResponse> {
    return new Promise<ApiErrorResponse>((resolve, reject) => {
      this.apiService
        .request<ApiErrorResponse, { username: string }>(
          '/auth/remember',
          'POST',
          {
            username,
          },
        )
        .then((response) => {
          if (response.error) {
            reject(response);
            return;
          }

          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private async setAuthData(data: AuthResponse): Promise<void> {
    await this.localService.saveDataV2('token', data.token);
    await this.localService.saveDataV2('user', data.user);
    this.currentToken = data.token;
    this.currentUser = data.user as AuthUser;
    this.apiService.setToken(data.token);
    this.changed.emit(true);
  }

  async startImpersonation(data: AuthResponse): Promise<void> {
    await this.hydrateAuthData();

    if (!this.currentToken || !this.currentUser || !this.isAdmin()) {
      throw new Error('Solo un administrador puede suplantar usuarios.');
    }

    this.impersonationAdminAuth = {
      token: this.currentToken,
      user: this.currentUser,
    };
    await this.localService.saveDataV2(
      this.impersonationStorageKey,
      this.impersonationAdminAuth,
    );
    await this.setAuthData(data);
  }

  async stopImpersonation(): Promise<boolean> {
    await this.hydrateAuthData();
    const adminAuth =
      this.impersonationAdminAuth ??
      (await this.localService.getDataV2<AuthResponse>(
        this.impersonationStorageKey,
      ));

    if (!adminAuth?.token || adminAuth.user?.role !== 1) {
      return false;
    }

    this.impersonationAdminAuth = null;
    this.localService.removeData(this.impersonationStorageKey);
    await this.setAuthData(adminAuth);
    return true;
  }

  isImpersonating(): boolean {
    return !!this.impersonationAdminAuth;
  }

  getImpersonatedUser(): AuthUser | null {
    return this.isImpersonating() ? this.currentUser : null;
  }

  clean() {
    this.localService.removeData('user');
    this.localService.removeData('token');
    this.localService.removeData(this.impersonationStorageKey);
    this.currentToken = null;
    this.currentUser = null;
    this.impersonationAdminAuth = null;
    this.apiService.setToken('');
    this.changed.emit(false);
  }

  isLogged(): boolean {
    return this.currentToken !== null;
  }

  async isLoggedAsync(): Promise<boolean> {
    await this.hydrateAuthData();
    return this.currentToken !== null;
  }

  getUser(): AuthUser | null {
    return this.currentUser;
  }

  async updateCurrentUser(user: AuthUser): Promise<void> {
    this.currentUser = user;
    await this.localService.saveDataV2('user', user);
    this.changed.emit(true);
  }

  async getUserAsync(): Promise<AuthUser | null> {
    await this.hydrateAuthData();
    return this.currentUser;
  }

  register(data: unknown): Promise<ApiErrorResponse> {
    return this.apiService.request<ApiErrorResponse, unknown>(
      '/auth/signup',
      'POST',
      data,
    );
  }

  isAdmin() {
    const user = this.getUser();
    return user?.role === 1;
  }

  private async hydrateAuthData(): Promise<void> {
    if (this.hydrationPromise) {
      return this.hydrationPromise;
    }

    this.hydrationPromise = (async () => {
      const [token, user, impersonationAdminAuth] = await Promise.all([
        this.localService.getDataV2<string>('token'),
        this.localService.getDataV2<AuthUser>('user'),
        this.localService.getDataV2<AuthResponse>(
          this.impersonationStorageKey,
        ),
      ]);

      this.currentToken = token;
      this.currentUser = user;
      this.impersonationAdminAuth =
        impersonationAdminAuth?.user?.role === 1
          ? impersonationAdminAuth
          : null;

      if (token) {
        this.apiService.setToken(token);
      }
    })();

    await this.hydrationPromise;
  }
}
